/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';

/**
 * Boot instrumentation for the app -> webview open (FCM-2662).
 *
 * Measures each leg of the boot (JS eval, bridge token, CreateSession, data
 * fetches, first rendered content) and reports one `webview_boot` transaction
 * to Sentry per open, tagged with platform / redirect target / cache-hit /
 * connection type, so p50/p95 can be split per dimension.
 *
 * Armed only when the page loads on /app-bridge (the app's entry point);
 * desktop and direct-browser flows never arm it and are unaffected.
 *
 * "Content" is detected centrally in useLoadingStates: the first time a
 * page's aggregate loading flag transitions true -> false after boot is the
 * destination's content render. If that never happens within 30s, a
 * `webview_boot_stuck` event fires with the last phase reached, which turns
 * "it hung" reports into "it hung at the token leg on iOS".
 *
 * `js_cache_hit` (main.js served with transferSize === 0) answers from the
 * field whether the app webviews reuse a persistent HTTP cache or re-download
 * the bundle on every open — the plan's biggest open question.
 *
 * Every entry point is guarded: telemetry must never break the boot.
 */

let armed = false;
let finished = false;
let sawLoading = false;
let stuckTimer: number | null = null;
const marks: Record<string, number> = {};
const tags: Record<string, string> = {};

const now = (): number => Math.round(performance.now());

const lastPhase = (): string =>
  Object.entries(marks).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'js_eval';

const finish = (outcome: 'ok' | 'failed' | 'stuck'): void => {
  if (!armed || finished) return;
  finished = true;
  if (stuckTimer !== null) window.clearTimeout(stuckTimer);

  try {
    const end = now();
    const nav = performance.getEntriesByType('navigation')[0] as
      PerformanceNavigationTiming | undefined;
    const mainJs = (
      performance.getEntriesByType('resource') as PerformanceResourceTiming[]
    ).find((r) => /\/static\/js\/main\.[^/]+\.js$|\/bundle\.js$/.test(r.name));
    const fcp = performance.getEntriesByName('first-contentful-paint')[0];

    const attrs: Record<string, string | number | boolean> = {
      ...tags,
      outcome
    };
    if (nav) attrs.html_response_ms = Math.round(nav.responseEnd);
    if (mainJs) {
      attrs.js_downloaded_ms = Math.round(mainJs.responseEnd);
      // String, not boolean: boolean span attributes came back null on every one
      // of the first 34 device boots (2026-07-20), so the cache question went
      // unanswered. Also record the raw bytes — a cache hit is transferSize 0
      // with a non-zero decoded body, and having both makes it self-checking.
      attrs.js_cache_hit = String(
        mainJs.transferSize === 0 && mainJs.decodedBodySize > 0
      );
      attrs.js_transfer_kb = Math.round(mainJs.transferSize / 1024);
      attrs.js_decoded_kb = Math.round(mainJs.decodedBodySize / 1024);
    }
    // With the HTML spinner disabled, FCP is React's first paint and can land
    // after this report runs — hence mostly absent since 2026-07-20. Treat a
    // missing value as "not yet painted", not as zero.
    if (fcp) attrs.first_paint_ms = Math.round(fcp.startTime);
    if (marks.token_start != null && marks.token_end != null) {
      attrs.token_ms = marks.token_end - marks.token_start;
    }
    if (marks.session_start != null && marks.session_end != null) {
      attrs.session_ms = marks.session_end - marks.session_start;
    }
    if (marks.session_end != null) attrs.data_ms = end - marks.session_end;
    attrs.total_ms = end;
    Object.entries(marks).forEach(([phase, at]) => {
      attrs[`mark_${phase}_ms`] = at;
    });

    const span = Sentry.startInactiveSpan({
      name: 'webview_boot',
      op: 'webview.boot',
      forceTransaction: true,
      startTime: performance.timeOrigin / 1000,
      attributes: attrs
    });
    span.end((performance.timeOrigin + end) / 1000);

    if (outcome !== 'ok') {
      Sentry.captureMessage(`webview_boot_${outcome}`, {
        level: 'warning',
        tags: { ...tags, last_phase: lastPhase() },
        extra: { marks }
      });
    }
  } catch {
    // Telemetry must never break the boot.
  }
};

/** Call once, as early as possible, when the page loads on /app-bridge. */
export const armBootTrace = (redirect: string): void => {
  try {
    if (armed) return;
    armed = true;
    marks.js_eval = now();
    tags.redirect = redirect.split('?')[0] || 'none';
    // Separates this build's boots from the pre-optimization baseline collected
    // on fcm-2662-boot-instrumentation-stg, which both deploy to staging under
    // the same Sentry release.
    tags.boot_variant = 'phase1';
    // NOT `platform`: that collides with Sentry's built-in field and the tag is
    // silently dropped (learned on the baseline run — group by `os.name` there).
    if (window.Android) {
      tags.boot_platform = 'android';
    } else if (window.webkit?.messageHandlers) {
      tags.boot_platform = 'ios';
    } else {
      tags.boot_platform = 'browser';
    }
    const connection = (navigator as any).connection?.effectiveType;
    if (connection) tags.connection = connection;
    stuckTimer = window.setTimeout(() => finish('stuck'), 30000);
  } catch {
    // never break boot
  }
};

/** Record a boot phase (token_start, session_end, ...). No-op when not armed. */
export const bootMark = (phase: string): void => {
  if (!armed || finished) return;
  marks[phase] = now();
  try {
    performance.mark(`webview_boot:${phase}`);
  } catch {
    // performance.mark can throw on reserved names in old engines
  }
};

/** Attach a dimension to the boot report (token_mode, fail_reason, ...). */
export const bootTag = (key: string, value: string): void => {
  if (!armed || finished) return;
  tags[key] = value;
};

/**
 * Fed by useLoadingStates with every aggregate-loading change. The first
 * true -> false transition after boot marks the destination's content render.
 * (Requiring a prior `true` avoids the first post-mount tick, when no fetch
 * has been dispatched yet and every flag is still false.)
 */
export const bootObserveLoading = (anyLoading: boolean): void => {
  if (!armed || finished) return;
  if (anyLoading) {
    sawLoading = true;
    return;
  }
  if (sawLoading) finish('ok');
};

/** Boot failed outright (token/session error) — report with the reason. */
export const bootFail = (reason: string): void => {
  if (!armed || finished) return;
  tags.fail_reason = reason;
  finish('failed');
};
